import { useState, useCallback } from "react";
import { Camera, X, Upload } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

interface PhotoUploaderProps {
  photos: string[];
  onPhotosChange: (photos: File[], previews: string[]) => void;
  maxPhotos?: number;
}

export const PhotoUploader = ({ photos, onPhotosChange, maxPhotos = 10 }: PhotoUploaderProps) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    
    const fileArray = Array.from(files);
    const remainingSlots = maxPhotos - photos.length;
    const filesToAdd = fileArray.slice(0, remainingSlots);
    
    const newFiles: File[] = [];
    const newPreviews: string[] = [];

    filesToAdd.forEach((file) => {
      if (file.type.startsWith('image/')) {
        newFiles.push(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews.push(reader.result as string);
          if (newPreviews.length === filesToAdd.length) {
            onPhotosChange(newFiles, [...photos, ...newPreviews]);
          }
        };
        reader.readAsDataURL(file);
      }
    });
  }, [photos, maxPhotos, onPhotosChange]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const removePhoto = (index: number) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    onPhotosChange([], newPhotos);
  };

  return (
    <div className="space-y-4">
      <Label>上传照片 * (最多{maxPhotos}张)</Label>
      
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer ${
          isDragging 
            ? 'border-primary bg-primary/10' 
            : 'border-border hover:border-primary'
        }`}
      >
        <input
          id="photo-upload"
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          disabled={photos.length >= maxPhotos}
        />
        <Label htmlFor="photo-upload" className="cursor-pointer">
          {isDragging ? (
            <Upload className="h-12 w-12 mx-auto mb-3 text-primary animate-bounce" />
          ) : (
            <Camera className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
          )}
          <p className="text-sm text-muted-foreground">
            {photos.length >= maxPhotos 
              ? `已达到最大数量（${maxPhotos}张）`
              : '点击选择或拖拽照片至此处'
            }
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            支持 JPG、PNG 格式，单张最大 10MB
          </p>
        </Label>
      </div>

      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {photos.map((preview, index) => (
            <div key={index} className="relative aspect-square rounded-lg overflow-hidden border shadow-sm group">
              <img src={preview} alt={`预览 ${index + 1}`} className="w-full h-full object-cover" />
              <Button
                size="icon"
                variant="destructive"
                className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => removePhoto(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
